import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  CartesianGrid,
  ZAxis,
} from "recharts";
import { EmployeeFormatter } from "../../../helper/formatting";
import { computeMetricRangeDomain, isMetricRenderable } from "./metricRangeDomain";
import CompanySidebarEmptyState from "./CompanySidebarEmptyState";
import { SIDEBAR } from "./sidebarVisual";
import { SidebarSectionTitle } from "./SidebarPrimitives";

const AXIS_TICK = "#9AA0A6";
const GRID_STROKE = "rgba(88, 89, 91, 0.10)";
const MUTED = "rgba(88, 89, 91, 0.45)";

const formatTick = (formatter, value) => {
  try {
    return formatter.format(value);
  } catch (_error) {
    return String(value);
  }
};

function RangeTooltip({ active, payload, formatter, metricLabel }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "8px",
        px: 1.35,
        py: 0.9,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12), 0 1px 2px rgba(15, 23, 42, 0.06)",
        border: "1px solid rgba(88, 89, 91, 0.08)",
        minWidth: 112,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: "text.primary",
          letterSpacing: "-0.01em",
          mb: 0.35,
          lineHeight: 1.2,
        }}
      >
        {point.label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: "primary.main",
          lineHeight: 1.25,
          display: "flex",
          gap: 0.75,
          alignItems: "baseline",
        }}
      >
        <Box component="span" sx={{ opacity: 0.85 }}>
          {metricLabel}
        </Box>
        <Box component="span">{formatTick(formatter, point.x)}</Box>
      </Typography>
    </Box>
  );
}

RangeTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  formatter: PropTypes.shape({ format: PropTypes.func }),
  metricLabel: PropTypes.string,
};

function EstimateDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={9} fill={fill} fillOpacity={0.14} />
      <circle cx={cx} cy={cy} r={5} fill={fill} stroke="#fff" strokeWidth={2} />
    </g>
  );
}

EstimateDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  fill: PropTypes.string,
};

function ObservationDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={3.5} fill={fill} fillOpacity={0.55} />;
}

ObservationDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  fill: PropTypes.string,
};

/**
 * Pin-inspired range / estimate visualization.
 * Uses only pointEstimate + CI bounds + observation values — never invents a time series.
 */
export default function MetricRangeChart({
  pointEstimate,
  lowerBound,
  upperBound,
  dataPoints = [],
  formatter = EmployeeFormatter,
  accentColor = "#4A2CF5",
  height = 96,
  title,
  metricLabel = "Estimate",
  compact = false,
}) {
  const valid = isMetricRenderable(pointEstimate, upperBound);

  if (!valid) {
    return (
      <Box data-testid="metric-range-chart-empty">
        {title ? (
          <SidebarSectionTitle sx={{ mb: SIDEBAR.gap.withinCard }}>{title}</SidebarSectionTitle>
        ) : null}
        <CompanySidebarEmptyState
          variant="chart"
          compact
          title="No range data"
          description="This estimate range isn’t available for this company."
          testId="metric-range-empty-state"
        />
      </Box>
    );
  }

  const { min, max } = computeMetricRangeDomain(
    pointEstimate,
    lowerBound,
    upperBound,
    dataPoints
  );
  const domain = [min, max];

  const observationData = (dataPoints || [])
    .filter((value) => typeof value === "number" && Number.isFinite(value))
    .map((value, index) => ({
      x: value,
      y: 0,
      label: "Observation",
      id: `obs-${index}`,
    }));

  const estimateData = [
    {
      x: pointEstimate,
      y: 0,
      label: "Point estimate",
      id: "estimate",
    },
  ];

  const hasBounds =
    typeof lowerBound === "number" &&
    typeof upperBound === "number" &&
    Number.isFinite(lowerBound) &&
    Number.isFinite(upperBound);

  return (
    <Box sx={{ width: "100%" }} data-testid="metric-range-chart">
      {title ? (
        <SidebarSectionTitle sx={{ mb: SIDEBAR.gap.withinCard }}>{title}</SidebarSectionTitle>
      ) : null}
      <Box sx={{ width: "100%", height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 14, right: compact ? 8 : 12, bottom: 6, left: 4 }}>
            <CartesianGrid horizontal={false} stroke={GRID_STROKE} strokeDasharray="0" />
            <XAxis
              type="number"
              dataKey="x"
              domain={domain}
              tickFormatter={(value) => formatTick(formatter, value)}
              tick={{ fontSize: compact ? 9 : 10, fill: AXIS_TICK, fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
              tickCount={compact ? 3 : 4}
              height={20}
            />
            <YAxis type="number" dataKey="y" domain={[-0.9, 0.9]} hide />
            <ZAxis range={[40, 40]} />
            {hasBounds ? (
              <ReferenceArea
                x1={lowerBound}
                x2={upperBound}
                y1={-0.38}
                y2={0.38}
                fill={accentColor}
                fillOpacity={0.09}
                stroke={accentColor}
                strokeOpacity={0.22}
                strokeWidth={1}
                ifOverflow="extendDomain"
              />
            ) : null}
            {hasBounds ? (
              <>
                <ReferenceLine
                  x={lowerBound}
                  stroke={MUTED}
                  strokeDasharray="3 4"
                  strokeWidth={1}
                />
                <ReferenceLine
                  x={upperBound}
                  stroke={MUTED}
                  strokeDasharray="3 4"
                  strokeWidth={1}
                />
              </>
            ) : null}
            <ReferenceLine
              x={pointEstimate}
              stroke={accentColor}
              strokeWidth={1.5}
              strokeOpacity={0.55}
            />
            <Tooltip
              trigger={compact ? "click" : "hover"}
              cursor={{
                stroke: accentColor,
                strokeOpacity: 0.28,
                strokeWidth: 1,
              }}
              content={<RangeTooltip formatter={formatter} metricLabel={metricLabel} />}
              isAnimationActive={false}
            />
            {observationData.length > 0 ? (
              <Scatter
                data={observationData}
                fill={accentColor}
                name="Observations"
                shape={(props) => <ObservationDot {...props} fill={accentColor} />}
                isAnimationActive={false}
              />
            ) : null}
            <Scatter
              data={estimateData}
              fill={accentColor}
              name="Estimate"
              shape={(props) => <EstimateDot {...props} fill={accentColor} />}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
      <Typography sx={{ ...SIDEBAR.type.meta, mt: 0.15 }}>
        {`Estimate ${formatTick(formatter, pointEstimate)}`}
        {hasBounds
          ? ` · Range ${formatTick(formatter, lowerBound)} – ${formatTick(formatter, upperBound)}`
          : ""}
      </Typography>
    </Box>
  );
}

MetricRangeChart.propTypes = {
  pointEstimate: PropTypes.number,
  lowerBound: PropTypes.number,
  upperBound: PropTypes.number,
  dataPoints: PropTypes.arrayOf(PropTypes.number),
  formatter: PropTypes.shape({ format: PropTypes.func }),
  accentColor: PropTypes.string,
  height: PropTypes.number,
  title: PropTypes.string,
  metricLabel: PropTypes.string,
  compact: PropTypes.bool,
};
